import AppState from 'AppState';
import React from 'react';
import { Field, FieldProps } from 'react-final-form';
import { useSortedCultures } from './CulturePicker';
import LocalizeMode from './LocalizeMode';
import withEditContext, { HasEditContext } from './withEditContext';
import classnames from 'classnames';
import { getErrorArray } from './fields/ValidationMessages';
import { toKebabCase } from '~/util/StringUtil';
import { TokenAvailability } from '../AvailableReplacementTokens';

export enum FallbackValueDisplayMode {
	Hidden,
	Inline,
	Toggle,
}

export interface TranslatedFieldProps extends FieldProps<any, any>, HasEditContext {
	primaryCulture: string;
	staticFallbackCulture?: boolean;
	staticFallbackLabel?: string;
	fallbackValueDisplayMode?: FallbackValueDisplayMode;
	translatedFieldPrefix?: string;
	translatedFieldNameOverride?: string;
	testId?: string;
	/**
	 * List of available replacement tokens.
	 */
	replacementTokenAvailabilities?: TokenAvailability[];
}

/**
 * A form field wrapper that adds support to set values to different languages.
 * @param props
 */
function TranslatedField(props: TranslatedFieldProps) {
	const {
		editContext: { entityDefinition, currentCulture, primaryCulture },
		isRequired,
		autoFocus,
		label,
		name,
		staticFallbackCulture,
		staticFallbackLabel,
		editContext,
		fallbackValueDisplayMode = FallbackValueDisplayMode.Inline,
		translatedFieldPrefix,
		translatedFieldNameOverride,
		testId,
		replacementTokenAvailabilities,
	} = props;

	const sortedCultures = useSortedCultures().map((culture) => ({ ...culture }));
	const primaryCultureObj = AppState.cultures.find((culture) => culture.id === primaryCulture);
	const singleCultureDisplayed = entityDefinition.localizeMode === LocalizeMode.SingleCulture;
	const useStaticCulture = staticFallbackCulture && !singleCultureDisplayed;

	if (useStaticCulture) {
		const fallback = sortedCultures.filter((c) => c.isFallback);
		if (fallback && fallback.length === 1) {
			sortedCultures.unshift({ ...fallback[0] });
		}
	}

	if (!entityDefinition.localizeMode || !currentCulture) {
		if (!currentCulture) {
			// console.warn('No culture defined on the record, returning wrapped field.');
		}

		return <Field {...props} />;
	}

	const fieldPrefix: string = translatedFieldPrefix ? translatedFieldPrefix + '.' : '';
	const fieldOverride: string = translatedFieldNameOverride ? translatedFieldNameOverride : name;

	return sortedCultures.map((culture, index) => {
		const fieldHidden = currentCulture !== culture.id && singleCultureDisplayed;
		const isStaticFallback = index === 0 && staticFallbackCulture;
		const isPrimary = culture.id === primaryCulture;
		const fieldIsRequired = isRequired && isPrimary;
		const fieldAutoFocus = autoFocus && isPrimary;
		const fieldName = isStaticFallback
			? name
			: isPrimary && !useStaticCulture
			? name
			: fieldPrefix + 'translations.' + culture.id + '.' + fieldOverride;
		let labelValue = isStaticFallback ? (staticFallbackLabel ? staticFallbackLabel : label) : label;
		const readOnly = isStaticFallback && !editContext.isCreateMode;

		if (!singleCultureDisplayed && !isStaticFallback) {
			labelValue += ` / ${culture.name} (${culture.code})`;
		}

		if (fieldHidden) {
			return null;
		}

		return (
			<Field name={name} key={`${culture.id}-${index}`}>
				{({ input: { value }, meta }) => (
					// We need to check if the field has a value, so that we can conditionally render the 'translated-field' class on the div
					<Field name={fieldName}>
						{({ input: { value: translationValue } }) => {
							const errors = getErrorArray(meta.submitError);

							return (
								<div
									className={classnames({
										'translated-field': !translationValue && currentCulture !== primaryCulture,
										'translated-field--error':
											meta.submitError &&
											meta.touched &&
											errors.length > 0 &&
											!translationValue &&
											currentCulture !== primaryCulture,
									})}
								>
									<Field
										{...props}
										parse={(value) => value}
										name={fieldName}
										label={labelValue}
										isRequired={fieldIsRequired}
										autoFocus={fieldAutoFocus}
										readOnly={readOnly}
										translatedFieldName={name !== fieldName ? name : undefined}
										translatedFieldValue={value}
										isShared={false}
										fallbackValueDisplayMode={fallbackValueDisplayMode}
										primaryCulture={primaryCultureObj}
										meta={meta}
										data-testid={testId || `translated-field-${toKebabCase(name)}`}
										replacementTokenAvailabilities={replacementTokenAvailabilities}
									/>
								</div>
							);
						}}
					</Field>
				)}
			</Field>
		);
	});
}

export default withEditContext(TranslatedField);
